<template>
  <Layout class="billing-layout" :show-upgrade-prompt-modal="false">
    <HeaderRoot title="Billing" />
    <div v-if="isLoadingBilling()" class="layout__wrapper">
      <section class="subscription-box">
        <loading-spinner />
      </section>
    </div>
    <div
      v-else-if="isErrorLoadingBilling() || !billing"
      class="layout__wrapper"
    >
      <section class="subscription-box">
        <LoadingError />
      </section>
    </div>
    <div v-else-if="isAppleWebBilling" class="layout__wrapper container">
      <ContentWrapper>
        You are subscribed with Apple in-app purchase subscription. Please use
        native iOS app or
        <a
          :href="billing.paymentProviderSettingsUrl"
          target="_blank"
          class="provider-settings-link"
        >
          Apple subscription interface
        </a>
        to manage your subscription.
      </ContentWrapper>
    </div>
    <div v-else-if="isGoogleWebBilling" class="layout__wrapper container">
      <ContentWrapper>
        You are subscribed through Google Play. Please use the native Android
        app or the
        <a
          :href="billing.paymentProviderSettingsUrl"
          target="_blank"
          class="provider-settings-link"
        >
          Google Play settings
        </a>
        to manage your subscription.
      </ContentWrapper>
    </div>
    <BillingDataFullAccess v-else-if="billing.hasFullAccess" />
    <BillingData v-else-if="isFree" :billing="billing" />
    <BillingDataPaid v-else-if="isStripePaid" :billing="billing" />
    <BillingDataPaidAppStore v-else-if="isApplePaid" :billing="billing" />
  </Layout>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, provide, ref, watch } from 'vue'
import type { Raw } from 'vue'
import { useWait } from '@/app/composables/useWait'
import { useRoute, useRouter } from 'vue-router'

import Layout from '@/app/components/Layout.vue' // @ is an alias to /src
import HeaderRoot from '@/app/components/header/Root.vue'
import BillingData from '@/app/components/billing/BillingData.vue'
import BillingDataPaid from '@/app/components/billing/BillingDataPaid.vue'
import BillingDataPaidAppStore from '@/app/components/billing/BillingDataPaidAppStore.vue'
import BillingDataFullAccess from '@/app/components/billing/BillingDataFullAccess.vue'
import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import ContentWrapper from '@/components/ui/ContentWrapper.vue'
import { assertNotNull } from '@/helpers/typing'
import { backend } from '@/services/backend'
import {
  interestBookKey,
  loadInterestBook,
  type InterestBook,
} from '@/helpers/billingSourceBook'
import { Sentry } from '@/services/sentry'
import {
  BillingView,
  createBilling,
  isAppleWeb,
  isGoogleWeb,
} from '@/models/billing'
import {
  iosExternalLinkPayment16218Variant,
  onboardingChecklist19055Experiment,
  onboardingChecklist19055Variant,
} from '@/services/ab'
import { checklist } from '@/models/checklist'

const route = useRoute()
const router = useRouter()
const wait = useWait()

const billing = ref<Raw<BillingView> | null>(null)
// For checkout modal personalization (book stack UI).
const interestBook = ref<InterestBook | null>(null)
provide(interestBookKey, interestBook)

const isAppleWebBilling = ref(false)
const isGoogleWebBilling = ref(false)

onBeforeMount(async () => {
  void trackBillingFirstView()
  void loadInterestBookForBilling()
  wait.start('Loading Billing')
  await refreshBilling()
})

async function loadInterestBookForBilling(): Promise<void> {
  try {
    interestBook.value = await loadInterestBook(
      route.query.source_book as string | undefined,
    )
  } catch (error) {
    Sentry.captureException(error)
  }
}

async function refreshBilling(): Promise<void> {
  wait.start('Loading Billing')
  wait.end('Error Loading Billing')
  try {
    const response = await backend.getBilling()
    billing.value = await createBilling(response)
    isAppleWebBilling.value = await isAppleWeb(response)
    isGoogleWebBilling.value = await isGoogleWeb(response)
  } catch (error) {
    wait.start('Error Loading Billing')
    // The error here is usually NetworkError or Timeout, but
    // we still capture all errors to Sentry to be aware if
    // error rate increases.
    Sentry.withScope(function (scope) {
      scope.setFingerprint(['billing-loading-error'])
      Sentry.captureException(error)
    })
  } finally {
    wait.end('Loading Billing')
  }
}

async function trackBillingFirstView(): Promise<void> {
  try {
    await backend.trackBillingFirstView()
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setFingerprint(['billing-first-view-track-error'])
      Sentry.captureException(error)
    })
  }
}

/**
 * Handles the redirect after subscription has been made.
 */
watch(
  () => billing.value?.subscribedAlertAndRedirect,
  async (value?: boolean /* , old: boolean */) => {
    if (value !== true) {
      return
    }

    let checklistExperimentVariant = onboardingChecklist19055Variant()
    if (!checklistExperimentVariant) {
      // This is one of 2 possible places where we can start this experiment.
      // The other place is in the Discover page. We start the experiment here
      // if the user didn't visit the Discover page before starting the trial.
      checklistExperimentVariant =
        await onboardingChecklist19055Experiment('trial')
    }

    // We used to have a six-second delay here, but it was removed after
    // exp_first_guide_activation_17817 was resolved.

    if (checklistExperimentVariant === 'B') {
      // Await the navigation before marking the checklist step as completed.
      // This prevents the notifiaction from being immediately dismissed from the
      // navigation.
      await router.push({ name: 'discover' })
      checklist()?.steps.start_trial?.addProgress(1)
    } else {
      router.push({ name: 'books' })
    }
  },
)

function isLoadingBilling(): boolean {
  return wait.is('Loading Billing')
}

function isErrorLoadingBilling(): boolean {
  return wait.is('Error Loading Billing')
}

const isFree = computed(() => {
  const billingValue = assertNotNull(billing.value)
  return !billingValue.isPaidPlan
})

const isStripePaid = computed(() => {
  const billingValue = assertNotNull(billing.value)

  const isAppleExternalBilling16218 =
    billingValue.billing_type === 'stripe' &&
    iosExternalLinkPayment16218Variant() === 'B'

  return (
    billingValue.isPaidPlan &&
    (!billingValue.isAppStore() || isAppleExternalBilling16218)
  )
})

const isApplePaid = computed(() => {
  const billingValue = assertNotNull(billing.value)
  return billingValue.isPaidPlan && billingValue.isAppStore()
})
</script>

<style scoped lang="scss">
.provider-settings-link {
  text-decoration: underline;
}

.content-wrapper-block {
  margin-top: var(--container-padding);
}
</style>
